import { useState, useEffect } from 'react';
import AppLayout from '../components/AppLayout';
import { Activity, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import type { HealthCheck } from '../../shared/types';
import { apiFetch } from '../lib/api';

export default function Health() {
  const [health, setHealth] = useState<HealthCheck | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHealth = async () => {
      try {
        const data = await apiFetch<HealthCheck>('/api/health');
        setHealth(data);
      } catch (error) {
        console.error('Failed to fetch health:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHealth();
    const interval = setInterval(fetchHealth, 10000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <AppLayout>
        <div className="p-6 flex items-center justify-center h-full">
          <p className="text-slate-400">Loading health data...</p>
        </div>
      </AppLayout>
    );
  }

  if (!health) {
    return (
      <AppLayout>
        <div className="p-6 flex items-center justify-center h-full">
          <p className="text-red-400">Failed to load health data</p>
        </div>
      </AppLayout>
    );
  }

  const formatUptime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const getStatusColor = (status: string) => {
    const lowerStatus = status.toLowerCase();
    if (lowerStatus.includes('connected') || lowerStatus.includes('operational') || lowerStatus.includes('active') || lowerStatus.includes('secure')) {
      return 'emerald';
    }
    if (lowerStatus.includes('disconnected') || lowerStatus.includes('error')) {
      return 'red';
    }
    return 'orange';
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'emerald';
    if (score >= 50) return 'orange';
    return 'red';
  };

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">System Health</h1>
          <p className="text-slate-400">Real-time diagnostics and status monitoring</p>
        </div>

        {/* Overall Health Score */}
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/50 rounded-lg p-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400 mb-2">Overall Health Score</p>
              <div className="flex items-baseline gap-2">
                <span className={`text-6xl font-bold text-${getScoreColor(health.health_score)}-400`}>
                  {health.health_score}
                </span>
                <span className="text-2xl text-slate-500">/100</span>
              </div>
            </div>
            <div className={`w-24 h-24 bg-${getScoreColor(health.health_score)}-500/20 rounded-full flex items-center justify-center`}>
              {health.health_score >= 80 ? (
                <CheckCircle2 className={`w-12 h-12 text-${getScoreColor(health.health_score)}-400`} />
              ) : (
                <AlertCircle className={`w-12 h-12 text-${getScoreColor(health.health_score)}-400`} />
              )}
            </div>
          </div>
        </div>

        {/* Service Status Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-slate-400">Plaid Integration</h3>
              <div className={`w-2 h-2 rounded-full bg-${getStatusColor(health.plaid_status)}-400`}></div>
            </div>
            <p className={`text-lg font-semibold text-${getStatusColor(health.plaid_status)}-400`}>
              {health.plaid_status}
            </p>
          </div>

          <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-slate-400">Moov Payments</h3>
              <div className={`w-2 h-2 rounded-full bg-${getStatusColor(health.moov_status)}-400`}></div>
            </div>
            <p className={`text-lg font-semibold text-${getStatusColor(health.moov_status)}-400`}>
              {health.moov_status}
            </p>
          </div>

          <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-slate-400">OCR Engine</h3>
              <div className={`w-2 h-2 rounded-full bg-${getStatusColor(health.ocr_status)}-400`}></div>
            </div>
            <p className={`text-lg font-semibold text-${getStatusColor(health.ocr_status)}-400`}>
              {health.ocr_status}
            </p>
          </div>

          <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-slate-400">Security Status</h3>
              <div className={`w-2 h-2 rounded-full bg-${getStatusColor(health.security_status)}-400`}></div>
            </div>
            <p className={`text-lg font-semibold text-${getStatusColor(health.security_status)}-400`}>
              {health.security_status}
            </p>
          </div>
        </div>

        {/* System Metrics */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-white mb-4">System Metrics</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-slate-400" />
                <p className="text-sm text-slate-400">Worker Uptime</p>
              </div>
              <p className="text-2xl font-bold text-white">{formatUptime(health.worker_uptime)}</p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-slate-400" />
                <p className="text-sm text-slate-400">API Response Time</p>
              </div>
              <p className="text-2xl font-bold text-white">&lt;100ms</p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4 text-slate-400" />
                <p className="text-sm text-slate-400">Success Rate</p>
              </div>
              <p className="text-2xl font-bold text-white">99.9%</p>
            </div>
          </div>
        </div>

        {/* Recent Activity Log */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Recent Activity</h2>
          <div className="space-y-3">
            {[
              { time: '2 min ago', event: 'Invoice scan completed', status: 'success' },
              { time: '15 min ago', event: 'Plaid connection verified', status: 'success' },
              { time: '1 hour ago', event: 'Finding detected: Duplicate billing', status: 'warning' },
              { time: '2 hours ago', event: 'Health check passed', status: 'success' },
            ].map((activity, index) => (
              <div key={index} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      activity.status === 'success' ? 'bg-emerald-400' : 'bg-orange-400'
                    }`}
                  ></div>
                  <span className="text-sm text-slate-300">{activity.event}</span>
                </div>
                <span className="text-xs text-slate-500">{activity.time}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
